import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useProducts } from "@/context/ProductsContext";
import { useMetaPixel } from "@/context/MetaPixelContext";
import { useAnalytics } from "@/context/AnalyticsContext";

const CartContext = createContext(null);

const STORAGE_KEY = "hk_cart_v1";

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load); // [{slug, qty}]
  const { allProducts } = useProducts();
  const pixel = useMetaPixel();
  const analytics = useAnalytics();
  const findProduct = (slug) => allProducts.find((p) => p.slug === slug) || null;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addItem = (slug, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((x) => x.slug === slug);
      const next = found
        ? prev.map((x) => (x.slug === slug ? { ...x, qty: x.qty + qty } : x))
        : [...prev, { slug, qty }];
      // Fire analytics using the freshly-projected next state so cart_total is accurate.
      try {
        const product = findProduct(slug);
        if (product) {
          const cart_total = next.reduce((sum, it) => {
            const p = findProduct(it.slug);
            return sum + (p?.price || 0) * it.qty;
          }, 0);
          analytics.fireAddToCart(product, qty, cart_total);
        }
      } catch {}
      return next;
    });
    // Meta Pixel AddToCart with real product data
    try {
      const product = findProduct(slug);
      if (product) {
        pixel.track("AddToCart", {
          content_ids: [product.slug],
          content_name: product.name,
          content_type: "product",
          value: product.price ? (product.price * qty) / 100 : undefined,
          currency: "INR",
          contents: [{ id: product.slug, quantity: qty, item_price: product.price ? product.price / 100 : undefined }],
        });
      }
    } catch {}
  };

  const setQty = (slug, qty) => {
    setItems((prev) =>
      qty <= 0 ? prev.filter((x) => x.slug !== slug) : prev.map((x) => (x.slug === slug ? { ...x, qty } : x))
    );
  };

  const remove = (slug) => setItems((prev) => prev.filter((x) => x.slug !== slug));
  const clear = () => setItems([]);

  const detailed = useMemo(() => {
    return items
      .map((it) => {
        const product = findProduct(it.slug);
        if (!product || product.status !== "available") return null;
        // BOGO: for sea buckthorn, physical qty = qty * 2 but price = qty * price
        const isBogo = product.slug === "sea-buckthorn-pure-pulp" && !!product.offer;
        const shippedUnits = isBogo ? it.qty * 2 : it.qty;
        const linePrice = (product.price || 0) * it.qty;
        return { ...it, product, shippedUnits, linePrice, isBogo };
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, allProducts]);

  const subtotal = detailed.reduce((s, x) => s + x.linePrice, 0);
  const totalUnits = detailed.reduce((s, x) => s + x.qty, 0);
  const totalShipped = detailed.reduce((s, x) => s + x.shippedUnits, 0);

  const value = { items: detailed, rawItems: items, addItem, setQty, remove, clear, subtotal, totalUnits, totalShipped };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

export const formatINR = (paise) =>
  "₹" + (paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
